import { useState, useEffect } from 'react';
import { TeamState, Player, SetRecord, Timeout } from '@/types';
import { checkRotationDeadlock } from '@/lib/validation';
import ScoringHeader from '@/components/ScoringHeader';

interface ServerSelectionModalProps {
  team: TeamState;
  eligibleServers: { eligible: Player[]; ineligible: { player: Player; remaining: number }[] };
  onSelect: (player: Player) => Promise<void>;
  reason: 'first-server' | 'service-break';
  onServeFoul?: (player: Player) => Promise<void>;
  onServerFault?: () => Promise<void>;
  onBack?: () => void;
  currentServer?: Player | null;
  rotationCooloff?: number;
  onTimeout?: () => void;
  // Scoring context for persistent header
  teamA: TeamState;
  teamB: TeamState;
  currentSetNumber: number;
  matchType: 'league' | 'playoff';
  currentScoreA: number;
  currentScoreB: number;
  completedSets: SetRecord[];
  completedTimeouts?: Timeout[];
}

export default function ServerSelectionModal({
  team,
  eligibleServers,
  onSelect,
  reason,
  onServeFoul,
  onServerFault,
  onBack,
  currentServer,
  rotationCooloff,
  onTimeout,
  teamA,
  teamB,
  currentSetNumber,
  matchType,
  currentScoreA,
  currentScoreB,
  completedSets,
  completedTimeouts = [],
}: ServerSelectionModalProps) {
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serveFoulMode, setServeFoulMode] = useState(false);

  // Reset serve foul mode when the team changes (after a serve foul, service passes to the opponent)
  useEffect(() => {
    setServeFoulMode(false);
    setError(null);
  }, [team.id]);

  // Check for rotation deadlock (only relevant for service-break, not first-server)
  // For first-server: new set, no rotation history exists yet, all players are eligible
  const hasDeadlock = reason === 'first-server' ? false : checkRotationDeadlock(team, rotationCooloff);

  // In serve foul mode every player is eligible (we just need to know who fouled)
  // For first-server of a new set, all players are eligible (no rotation history)
  const effectiveEligible = serveFoulMode || reason === 'first-server'
    ? team.players
    : hasDeadlock
      ? team.players
      : eligibleServers.eligible;

  const handleSelect = async (player: Player) => {
    setSelectingId(player.id);
    setError(null);

    try {
      if (serveFoulMode && onServeFoul) {
        await onServeFoul(player);
        // Modal stays open; team will change and useEffect will reset serveFoulMode
      } else {
        await onSelect(player);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid selection');
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50">
      <div className="absolute bottom-0 left-0 right-0 bg-surface border-t border-border rounded-t-2xl p-4 pb-safe max-h-[80vh] overflow-y-auto animate-slide-up">
        <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />

        {/* Persistent Scoring Header */}
        <ScoringHeader
          teamA={teamA}
          teamB={teamB}
          currentSetNumber={currentSetNumber}
          matchType={matchType}
          currentScoreA={currentScoreA}
          currentScoreB={currentScoreB}
          completedSets={completedSets}
          servingTeamId={team.id}
          completedTimeouts={completedTimeouts}
          compact
        />

        <h2 className="text-lg font-bold text-text-primary text-center mb-1">
          {serveFoulMode
            ? 'WHO SERVED THE FOUL?'
            : reason === 'first-server'
              ? 'SELECT FIRST SERVER'
              : 'SELECT NEXT SERVER'}
        </h2>

        {serveFoulMode ? (
          <p className="text-error text-sm text-center mb-2">
            Select the player who committed the serve foul.
          </p>
        ) : reason === 'service-break' ? (
          <p className="text-text-secondary text-sm text-center mb-2">
            {team.name} now serve. Choose the server.
          </p>
        ) : null}

        {serveFoulMode && (
          <div className="bg-error/20 border border-error rounded-lg p-2 mb-4">
            <p className="text-sm text-error text-center">
              Opponent receives 1 point. Service changes to opponent.
            </p>
          </div>
        )}

        {!serveFoulMode && hasDeadlock && (
          <div className="bg-warning/20 border border-warning rounded-lg p-2 mb-4">
            <p className="text-sm text-warning text-center">
              Rotation cycle reset. All players eligible.
            </p>
          </div>
        )}

        {/* Team Header */}
        <div
          className="rounded-lg p-3 mb-4"
          style={{ backgroundColor: `${team.color}15`, borderColor: team.color, borderWidth: 1 }}
        >
          <p className="font-semibold text-center" style={{ color: team.color }}>
            {team.name.toUpperCase()}
          </p>
        </div>

        {/* Player Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {team.players.map((player) => {
            const isEligible = effectiveEligible.some((p) => p.id === player.id);
            const ineligibleInfo = !serveFoulMode
              ? eligibleServers.ineligible.find((i) => i.player.id === player.id)
              : undefined;
            const isSelected = selectingId === player.id;

            return (
              <button
                key={player.id}
                onClick={() => isEligible && handleSelect(player)}
                disabled={!isEligible || selectingId !== null}
                className={`relative py-5 px-3 rounded-lg font-bold text-xl transition-all min-h-[72px] ${
                  !isEligible
                    ? 'bg-gray-800 border-2 border-gray-700 text-gray-500 cursor-not-allowed'
                    : isSelected
                      ? 'text-white active:scale-[0.98]'
                      : serveFoulMode
                        ? 'bg-surface-raised active:scale-[0.98] text-white'
                        : 'bg-surface-raised active:scale-[0.98] text-white'
                }`}
                style={
                  isEligible
                    ? isSelected
                      ? { backgroundColor: team.color, border: `4px solid ${team.color}` }
                      : serveFoulMode
                        ? { border: `4px solid #ef4444` }
                        : { border: `4px solid ${team.color}` }
                    : {}
                }
              >
                {isSelected ? (
                  <span className="flex items-center justify-center gap-2">
                    <span
                      className="w-4 h-4 border-2 rounded-full animate-spin"
                      style={{ borderColor: '#ffffff', borderTopColor: 'transparent' }}
                    />
                    {player.name}
                  </span>
                ) : (
                  <>
                    <span className={isEligible ? 'text-white' : 'text-gray-500'}>
                      {player.name}
                    </span>
                    {!isEligible && ineligibleInfo && (
                      <span className="block text-xs text-gray-500 mt-1 font-medium">
                        Cooldown ({ineligibleInfo.remaining} more)
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="bg-error/20 border border-error rounded-lg p-3 mb-4">
            <p className="text-error text-sm text-center">{error}</p>
          </div>
        )}

        {serveFoulMode ? (
          <button
            onClick={() => { setServeFoulMode(false); setError(null); }}
            className="btn-secondary text-text-secondary"
          >
            BACK
          </button>
        ) : (
          <div className="space-y-2">
            {/* SERVE FOUL: Current server exists - they committed the foul */}
            {onServeFoul && reason === 'service-break' && currentServer && (
              <button
                onClick={() => handleSelect(currentServer)}
                disabled={selectingId !== null}
                className="w-full py-3 px-4 rounded-lg border-2 border-error text-error font-bold text-sm transition-all active:scale-[0.98] disabled:opacity-50"
              >
                SERVE FOUL
              </button>
            )}
            {/* SERVER FAULT: No valid server yet - ineligible player served */}
            {onServerFault && reason === 'service-break' && !currentServer && (
              <button
                onClick={async () => {
                  setSelectingId('server-fault');
                  setError(null);
                  try {
                    await onServerFault();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to record server fault');
                  } finally {
                    setSelectingId(null);
                  }
                }}
                disabled={selectingId !== null}
                className="w-full py-3 px-4 rounded-lg border-2 border-error text-error font-bold text-sm transition-all active:scale-[0.98] disabled:opacity-50"
              >
                SERVER FAULT
              </button>
            )}
            {onBack && reason === 'first-server' && (
              <button
                onClick={onBack}
                disabled={selectingId !== null}
                className="w-full py-3 px-4 rounded-lg border border-border text-text-secondary font-semibold text-sm transition-all hover:bg-surface-raised"
              >
                BACK
              </button>
            )}
            {onTimeout && reason === 'service-break' && (
              <button
                onClick={onTimeout}
                disabled={selectingId !== null}
                className="w-full py-3 px-4 rounded-lg border border-warning text-warning font-bold text-sm transition-all active:scale-[0.98] disabled:opacity-50"
              >
                TIMEOUT
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
